/**
 * Gauge — a value read against a minimum, maximum, and optional target.
 *
 * Drawn as a 240° SVG arc so it scales with the tile without a chart library.
 * The needle colour follows the configured thresholds (highest threshold at or
 * below the value wins), falling back to the accent colour.
 */

import { useSourceFields } from './useSourceFields';
import { useWidgetData } from './useWidgetData';
import WidgetFrame from './WidgetFrame';
import { combineWhere, useIncomingConstraint, type ActionTargetProps } from './useActions';

import { getDataSourceAdapter, type StatisticResult } from '../services/dashboardDataService';
import { buildWhereClause, formatNumber, validateWidget } from '../utils/dashboardUtils';
import type { DashboardDataSource, DashboardWidget } from '../types/dashboardTypes';

interface Props {
  widget: DashboardWidget;
  source?: DashboardDataSource;
  refreshInterval: number;
  accentColor: string;
  dividerColor?: string;
  selectorWhere?: string;
}

type AllProps = Props & ActionTargetProps;

/** Arc geometry: 240° sweep, opening downwards. */
const START_ANGLE = 150;
const SWEEP = 240;
const RADIUS = 42;
const CENTRE = { x: 50, y: 50 };

function polarPoint(angleDegrees: number, radius = RADIUS) {
  const radians = (angleDegrees * Math.PI) / 180;
  return {
    x: CENTRE.x + radius * Math.cos(radians),
    y: CENTRE.y + radius * Math.sin(radians),
  };
}

function arcPath(fromAngle: number, toAngle: number, radius = RADIUS): string {
  const start = polarPoint(fromAngle, radius);
  const end = polarPoint(toAngle, radius);
  const largeArc = Math.abs(toAngle - fromAngle) > 180 ? 1 : 0;
  return `M ${start.x} ${start.y} A ${radius} ${radius} 0 ${largeArc} 1 ${end.x} ${end.y}`;
}

export default function GaugeWidget({
  widget,
  source,
  refreshInterval,
  accentColor,
  dividerColor,
  selectorWhere,
  incomingSignals,
  onClearSignals,
}: AllProps) {
  const { fields } = useSourceFields(source);
  const problems = validateWidget(widget);

  const statistic = widget.config.statistic ?? 'count';
  const incoming = useIncomingConstraint(incomingSignals, fields);
  const where = combineWhere(combineWhere(buildWhereClause(widget.filters, fields), incoming.where), selectorWhere ?? '');
  const enabled = Boolean(source) && problems.length === 0;

  const { data, status, error, reload } = useWidgetData<StatisticResult>(
    () =>
      getDataSourceAdapter(source as DashboardDataSource).fetchStatistic(
        source as DashboardDataSource,
        { statistic, field: widget.config.field, where, extent: incoming.extent, geometry: incoming.geometry },
      ),
    `gauge|${source?.id}|${statistic}|${widget.config.field ?? ''}|${where}|${JSON.stringify(incoming.extent ?? null)}|${incoming.geometry ? 'geom' : ''}`,
    refreshInterval,
    enabled,
  );

  const min = widget.config.minValue ?? 0;
  const max = widget.config.maxValue ?? 100;
  const value = data?.value ?? null;

  // Clamped so an out-of-range reading pins to an end rather than drawing a
  // needle outside the dial.
  const ratio =
    value === null || max <= min ? 0 : Math.max(0, Math.min(1, (value - min) / (max - min)));

  const thresholds = [...(widget.config.thresholds ?? [])].sort((a, b) => a.value - b.value);
  const activeThreshold = value === null
    ? undefined
    : thresholds.filter((entry) => value >= entry.value).pop();
  const valueColor = activeThreshold?.color || widget.config.color || accentColor;

  const valueAngle = START_ANGLE + SWEEP * ratio;
  const targetRatio =
    widget.config.targetValue !== undefined && max > min
      ? Math.max(0, Math.min(1, (widget.config.targetValue - min) / (max - min)))
      : null;

  return (
    <WidgetFrame
      title={widget.title}
      status={status}
      error={error}
      onRetry={reload}
      incomplete={problems}
      accentColor={accentColor}
      outlineColor={widget.config.outlineColor ?? dividerColor}
      actionLabels={incoming.labels}
      onClearActions={() => onClearSignals?.(incoming.sourceIds)}
      footnote={[source?.name, data?.truncated ? 'partial — first 5,000 records' : '']
        .filter(Boolean)
        .join(' · ')}
    >
      <div className="flex h-full flex-col items-center justify-center px-2 py-1">
        <svg viewBox="0 0 100 72" className="w-full max-w-[220px]" role="img" aria-label={widget.title}>
          {/* track */}
          <path
            d={arcPath(START_ANGLE, START_ANGLE + SWEEP)}
            fill="none"
            stroke="var(--bv-line-strong)"
            strokeWidth={9}
            strokeLinecap="round"
          />
          {/* value */}
          {ratio > 0 && (
            <path
              d={arcPath(START_ANGLE, valueAngle)}
              fill="none"
              stroke={valueColor}
              strokeWidth={9}
              strokeLinecap="round"
            />
          )}
          {/* target marker */}
          {targetRatio !== null && (
            <line
              x1={polarPoint(START_ANGLE + SWEEP * targetRatio, RADIUS - 7).x}
              y1={polarPoint(START_ANGLE + SWEEP * targetRatio, RADIUS - 7).y}
              x2={polarPoint(START_ANGLE + SWEEP * targetRatio, RADIUS + 7).x}
              y2={polarPoint(START_ANGLE + SWEEP * targetRatio, RADIUS + 7).y}
              stroke="var(--glass-text)"
              strokeWidth={1.6}
            />
          )}
          <text
            x={CENTRE.x}
            y={CENTRE.y + 4}
            textAnchor="middle"
            className="fill-slate-900"
            style={{ fontSize: 15, fontWeight: 600 }}
          >
            {widget.config.prefix}
            {formatNumber(value, widget.config.decimals ?? 0)}
            {widget.config.suffix}
          </text>
          <text x={12} y={68} textAnchor="middle" style={{ fontSize: 6 }} className="fill-slate-400">
            {formatNumber(min)}
          </text>
          <text x={88} y={68} textAnchor="middle" style={{ fontSize: 6 }} className="fill-slate-400">
            {formatNumber(max)}
          </text>
        </svg>

        {widget.config.targetValue !== undefined && (
          <p className="mt-0.5 text-[11px] text-slate-500">
            Target {formatNumber(widget.config.targetValue, widget.config.decimals ?? 0)}
            {value !== null && (
              <span className={value >= widget.config.targetValue ? 'text-teal-600' : 'text-amber-600'}>
                {' '}
                · {value >= widget.config.targetValue ? 'met' : 'below'}
              </span>
            )}
          </p>
        )}
      </div>
    </WidgetFrame>
  );
}
